/**
 * Theme controller for the test application (Story 301).
 *
 * Persists the user's theme choice in `localStorage` under the key
 * `cwc-theme` and syncs the `data-theme` attribute on the document root.
 * The reactive accessor returns a Vue ref so components can bind without
 * subscribing to storage events.
 */
import { ref, watch, type Ref } from 'vue';

export type ThemeName = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'cwc-theme';
export const DEFAULT_THEME: ThemeName = 'light';

/**
 * The storage key `@design-system/vue-ui`'s `ThemeProvider` owns internally
 * (a VueUse `useColorMode({ storageKey: 'design-system-theme', ... })`,
 * verified in the compiled bundle). It is fully decoupled from
 * {@link THEME_STORAGE_KEY} — the app must keep both in sync (016.2 AC3) so
 * `ThemeProvider` never resolves a stale/system-default mode that disagrees
 * with the app's own persisted choice.
 */
export const DESIGN_SYSTEM_THEME_STORAGE_KEY = 'design-system-theme';

/**
 * Reads the persisted theme from `localStorage`, falling back to
 * {@link DEFAULT_THEME} if missing or invalid.
 *
 * Accepts an injected storage instance to keep the function testable in
 * environments without `globalThis.localStorage` (Node, jsdom edge cases).
 */
export function readStoredTheme(storage: Storage | undefined): ThemeName {
  if (!storage) {
    return DEFAULT_THEME;
  }
  try {
    const raw = storage.getItem(THEME_STORAGE_KEY);
    if (raw === 'light' || raw === 'dark') {
      return raw;
    }
  } catch {
    // localStorage unavailable (e.g. Safari private mode) — fall through.
  }
  return DEFAULT_THEME;
}

/**
 * Writes the theme to storage. Swallows quota and security exceptions so a
 * failed persist never crashes the UI.
 */
export function writeStoredTheme(storage: Storage | undefined, theme: ThemeName): void {
  if (!storage) {
    return;
  }
  try {
    storage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // ignore — persistence is best-effort.
  }
}

/**
 * Writes the theme into the design system's own storage key, keeping
 * `ThemeProvider`'s `useColorMode` state in sync with the app's theme
 * choice (016.2 AC3) — see {@link DESIGN_SYSTEM_THEME_STORAGE_KEY}.
 */
function writeDesignSystemTheme(storage: Storage | undefined, theme: ThemeName): void {
  if (!storage) {
    return;
  }
  try {
    storage.setItem(DESIGN_SYSTEM_THEME_STORAGE_KEY, theme);
  } catch {
    // ignore — persistence is best-effort.
  }
}

/**
 * Seeds the design system's storage key from the app's persisted theme at
 * boot, before `ThemeProvider` mounts and its `useColorMode` reads that key
 * for its initial value. Without this, a returning user with a persisted
 * `cwc-theme` would see `ThemeProvider` briefly resolve to the system
 * default instead of their saved preference (016.2 AC3).
 */
export function seedDesignSystemTheme(storage: Storage | undefined = globalThis.localStorage): void {
  writeDesignSystemTheme(storage, readStoredTheme(storage));
}

/**
 * Applies the theme to the document root.
 *
 * Sets both the app's existing `data-theme` attribute (what `tokens.css`/
 * `theme.css` key their dark overrides off) and the design system's `.dark`
 * class (what `@design-system/vue-ui`'s token layer keys off via
 * `@custom-variant dark (&:is(.dark *))`) — two different hooks on the same
 * root element. This bridge (016.2 AC3) keeps every not-yet-migrated view's
 * dark mode working through the 016.3-016.9 restyle window; 016.10 AC1
 * removes the `data-theme` half once no view references `--gl-*` anymore.
 *
 * Safe to call before mount — `useTheme()` re-applies on every change via a
 * Vue watcher. If the document is not available (SSR / unit tests outside
 * jsdom) the call is a no-op.
 */
/**
 * Resolves a design-system `BasicColorSchema` ('auto' | 'light' | 'dark') to
 * the legacy bridge's binary {@link ThemeName}. The legacy `data-theme`/
 * `cwc-theme` system has no concept of "auto" — it resolves 'auto' to the
 * OS preference via `prefers-color-scheme`, matching what `useColorMode`
 * itself would resolve to if it ever emitted 'auto' (016.4 typecheck fix).
 */
export function resolveThemeName(scheme: 'auto' | ThemeName): ThemeName {
  if (scheme !== 'auto') {
    return scheme;
  }
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyTheme(theme: ThemeName): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

/**
 * Reactive theme controller.
 *
 * Returns a `theme` ref and a `toggle()` function that flips between
 * light and dark, persisting the choice and updating the document.
 */
export interface ThemeController {
  readonly theme: Ref<ThemeName>;
  toggle(): void;
  set(theme: ThemeName): void;
}

export function useTheme(storage: Storage | undefined = globalThis.localStorage): ThemeController {
  const theme = ref<ThemeName>(readStoredTheme(storage));

  watch(
    theme,
    (next) => {
      applyTheme(next);
      writeStoredTheme(storage, next);
      writeDesignSystemTheme(storage, next);
    },
    { immediate: true },
  );

  function toggle(): void {
    theme.value = theme.value === 'light' ? 'dark' : 'light';
  }

  function set(next: ThemeName): void {
    theme.value = next;
  }

  return { theme, toggle, set };
}
